import axios, { DefaultResponse, TypedResponse } from "../general/HttpClient";
import { IdentityUser, Organization } from "../users/UserStore";

export interface OrgChartNode {
    id: string;
    name: string;
    // Null for a user who owns no group: the org chart only stamps a node with the
    // position its user owns.
    groupID: string | null;
    groupName: string | null;
    groupTypeName: string | null;
    children: OrgChartNode[];
}

export function GetGroups(organizationId, groupTypes = "", groupFilter = ""): Promise<Group[]> {
    return axios
        .get(`/api/groups/${organizationId}`, {
            params: { groupTypes, groupFilter },
        })
        .then((res) => res.data);
}

export function GetOrgChart(organizationId, groupTypes = ""): Promise<OrgChartNode[]> {
    return axios
        .get(`/api/groups/${organizationId}/orgchart`, {
            params: { groupTypes },
        })
        .then((res) => res.data);
}

export function GetGroup(organizationId, groupId): Promise<Group> {
    return axios.get(`/api/groups/${organizationId}/groups/${groupId}`).then((res) => res.data);
}

export function GetGroupCustomTypes(organizationId): Promise<GroupCustomType[]> {
    return axios.get(`/api/groups/${organizationId}/groupTypes`).then((res) => res.data);
}

export function GetOrganizationMembers(organizationId, groupId): Promise<GetUsersResponse> {
    return axios
        .get(`/api/groups/${organizationId}/allmembers`, {
            params: {
                query: "",
                filter: groupId,
                skip: 0,
                limit: 0,
                delegated: false,
                allowStaff: true,
                allowStudents: true,
                allowParents: true,
                allowGuests: true,
            },
        })
        .then((res) => res.data);
}

export function CreateGroup(organizationId, data: CreateGroupRequest): Promise<TypedResponse<Group>> {
    return axios.post(`/api/groups/${organizationId}`, data).then((res) => res.data);
}

export function UpdateGroup(organizationId, groupId, data: UpdateGroupRequest): Promise<TypedResponse<Group>> {
    return axios.put(`/api/groups/${organizationId}/${groupId}`, data).then((res) => res.data);
}

export function AddMember(organizationId, groupId, data: CreateGroupMemberRequest): Promise<DefaultResponse> {
    return axios.post(`/api/groups/${organizationId}/${groupId}/members`, data).then((res) => res.data);
}

export function RemoveMember(organizationId, groupId, userId): Promise<DefaultResponse> {
    return axios.delete(`/api/groups/${organizationId}/${groupId}/members/${userId}`).then((res) => res.data);
}

export function RemoveAllMembers(organizationId: string, groupId: string): Promise<DefaultResponse> {
    return axios.delete(`/api/groups/${organizationId}/${groupId}/members`).then((res) => res.data);
}

export interface ImportGroupMembersResponse extends DefaultResponse {
    addedCount: number;
    removedCount: number;
    members: { identifier: string; userID: string; name: string; status: string; isDelegated: boolean }[];
}

export function ImportGroupMembers(organizationId: string, groupId: string, members: string[], preview: boolean, delegated = false, remove = false): Promise<ImportGroupMembersResponse> {
    return axios.post(`/api/groups/${organizationId}/${groupId}/members/${remove ? "remove" : "import"}`, { members, preview, delegated }).then((res) => res.data);
}

export function CreateGroupType(organizationId, data: CreateGroupTypeRequest): Promise<TypedResponse<Group>> {
    return axios.post(`/api/groups/${organizationId}/groupTypes`, data).then((res) => res.data);
}

export function GetDynamicGroupFields(organizationId: string): Promise<DynamicGroupFieldOption[]> {
    return axios.get(`/api/groups/${organizationId}/dynamicGroupBuilder/fields`).then((res) => res.data);
}

export function PreviewDynamicGroups(organizationId: string, condition: string): Promise<DynamicGroupPreview> {
    return axios.post(`/api/groups/${organizationId}/dynamicGroupBuilder/preview`, { condition }).then((res) => res.data);
}

export function CreateDynamicGroupType(organizationId: string, data: CreateDynamicGroupTypeRequest): Promise<TypedResponse<DynamicGroupTypeDefinition>> {
    return axios.post(`/api/groups/${organizationId}/dynamicGroupTypes`, data).then((res) => res.data);
}

export function DeleteGroup(id): Promise<TypedResponse<Group>> {
    return axios.delete(`/api/groups/${id}`).then((res) => res.data);
}

export function AddChild(organizationId, groupId, data: CreateChildGroupRequest): Promise<DefaultResponse> {
    return axios.post(`/api/groups/${organizationId}/${groupId}/children`, data).then((res) => res.data);
}

export function RemoveChild(organizationId, groupId, childId): Promise<DefaultResponse> {
    return axios.delete(`/api/groups/${organizationId}/${groupId}/children/${childId}`).then((res) => res.data);
}

export interface CreateGroupRequest {
    name: string;
    typeID: string;
}

export interface UpdateGroupRequest {
    name: string;
    description: string;
}

export interface CreateGroupTypeRequest {
    name: string;
}

export interface CreateDynamicGroupTypeRequest {
    name: string;
    condition: string;
}

export interface DynamicGroupTypeDefinition {
    id: string;
    name: string;
    organizationID: string;
    condition: string;
}

export interface DynamicGroupFieldOption {
    key: string;
    valueCount: number;
    memberCount: number;
}

export interface DynamicGroupPreview {
    totalGroups: number;
    totalMembers: number;
    isTruncated: boolean;
    groups: DynamicGroupPreviewGroup[];
}

export interface DynamicGroupPreviewGroup {
    name: string;
    memberCount: number;
}

export interface CreateGroupMemberRequest {
    userID: string;
    type: GroupMembershipType;
}

export interface CreateChildGroupRequest {
    groupID: string;
}

export interface Group {
    id: string;
    name: string;
    type: GroupType;
    organizationID: string;
    organization: Organization;
    source: string;
    sourceID: string;
    groupCustomTypeID: string;
    groupCustomType: GroupCustomType;
    data: GroupData;
    groupUsers: GroupUserMembership[];
    users: IdentityUser[];
    children: GroupGroupMembership[];
    childGroups: Group[];
    parents: GroupGroupMembership[];
    parentGroups: Group[];
    groupTags: GroupTagMembership[];
    tags: GroupTag[];
    allUsers: GroupUserMembership[];
}

export interface GroupData {
    description: string;
}

export enum GroupType {
    Default = 0,
    Custom = 1,
    Roster = 3,
    Class = 4,
    Dynamic = 5,
}

export const GroupTypeMap = {
    0: "Default",
    1: "Custom",
    3: "Roster",
    4: "Class",
    5: "Dynamic",
};

export interface GroupCustomType {
    id: string;
    name: string;
    organizationID: string;
    organization: Organization;
    source: string;
    sourceID: string;

    groupCount: number;
    isDynamic: boolean;
}

export interface GroupTag {
    id: string;
    name: string;
    organizationID: string;
    organization: Organization;
    groups: Group[];
}

export interface GroupUserMembership {
    groupID: string;
    group: Group;
    memberID: string;
    member: IdentityUser;
    membershipType: GroupMembershipType;
    source: string;
}

export enum GroupMembershipType {
    Member = 0,
    Owner = 1,
}

export interface GroupGroupMembership {
    groupID: string;
    group: Group;
    memberID: string;
    member: Group;
}

export interface GroupTagMembership {
    groupID: string;
    group: Group;
    tagId: string;
    tag: GroupTag;
}

export interface GetUsersResponse {
    members: IdentityUser[];
    total: number;
    success: boolean;
    message: string;
}

export const ImportGroupsFromExcel = (organizationId: string, excelFileData: string) => {
    return axios.post(
        `api/groups/importGroupsFromExcel`,
        { excelFileData },
        {
            params: {
                organizationId,
            },
        }
    );
};

export const ImportGroupsFromExcelTestRun = (organizationId: string, excelFileData: string) => {
    return axios.post(
        `api/groups/importGroupsFromExcelTestRun`,
        { excelFileData },
        {
            params: {
                organizationId,
            },
        }
    );
};
